import useSWR from "swr";
import { useAppStore } from "@/stores";
import { MANAGEMENT_LANGUAGE } from "@revelate/constants/management-language";
import { requestRevenueIntelligenceAnswerComposition } from "./revenue-intelligence-semantic-planner";
import { getRevenueIntelligenceChatAccessToken } from "./revenue-intelligence-chat-session";
import { requestOutlookReadout } from "../target-outlook/readout-request";
import { validateManagementWording, validateWordingBatch, type WordingSlot } from "./managementWording";

/** Shared, version-keyed gateway for new management-copy surfaces. Existing auth,
 * permissions, endpoint, model, timeout, retry and budget; no new API contract. */
export function useManagementWording({ namespace, packet, question, slots, ready }: {
  namespace: string; packet: string | null; question: string; slots: readonly WordingSlot[]; ready: boolean;
}) {
  const user = useAppStore(state => state.currentUser);
  const allowed = user && ["admin", "super_admin"].includes(user.role);
  const result = useSWR(ready && allowed && packet ? [namespace, MANAGEMENT_LANGUAGE.id, MANAGEMENT_LANGUAGE.version,
    user.company_id, user.id, packet, question] : null, async () => {
    const { supabaseClient } = await import("@/lib/supabase/client");
    const accessToken = await getRevenueIntelligenceChatAccessToken(() => supabaseClient.auth.getSession());
    if (!accessToken || !user || !packet) throw new Error("Sign in to prepare the written assessment.");
    return requestOutlookReadout(async signal => {
      const response = await requestRevenueIntelligenceAnswerComposition({ accessToken, companyId: String(user.company_id),
        conversationId: "target-outlook", approvedAnswer: packet, question, signal });
      if (!validateWordingBatch(slots, response.answer)) {
        if (import.meta.env?.DEV) {
          // Diagnostics contain only authored slot IDs/reason codes, never prose,
          // customer data, facts, credentials or raw API responses.
          let parsed: Record<string, unknown> = {};
          try { parsed = JSON.parse(response.answer); } catch { /* shape error */ }
          const issues = slots.flatMap(slot => {
            let reason = "meaning/shape";
            return validateManagementWording(slot, parsed?.[slot.id], code => { reason = code; }) === null ? [`${slot.id}:${reason}`] : [];
          });
          console.warn("Management wording rejected for", namespace, issues.join(", "));
        }
        throw new Error("Written assessment did not preserve the supplied meaning.");
      }
      return response;
    });
  }, { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false, keepPreviousData: false,
    errorRetryCount: 0, dedupingInterval: 86_400_000 });
  const values = ready ? validateWordingBatch(slots, result.data?.answer) : null;
  return { values, composed: values !== null, loading: result.isLoading || result.isValidating || !ready,
    error: result.error, retry: () => void result.mutate() };
}
